import md5 from "md5";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { useUserPreferences } from "../../../containers/UserPreferencesContainer";
import { fetchAircraftWithCache, fetchRamp } from "../../../hooks/api";
import { extractImportantLocationState } from "../../../hooks/useHangar";
import { useStateHistory } from "../../../hooks/useMeaningfulStateHistory";
import { usePredefinedObstacle } from "../../../hooks/usePredefinedObstacle";
import { useScheduleUrl } from "../../../hooks/useScheduleUrl";
import { PlacementService } from "../../../services/PlacementService";
import { useScheduleTimeRange } from "../../../stores/useScheduleTimeRange";
import { Aircraft, Ramp, Stack as StackType, Tenant } from "../../../types";
import { defaultPreferenceForAircraft } from "../../../utils";
import { detectAndLogDuplicates } from "../../../utils/schedulingHelpers";
import { getSpotForPositionLegacy } from "../../../utils/spots";
import { ceilToQuarterHour } from "../../../utils/timeUtils";
import { useLayout } from "../../Ramp/CollapsibleRampActionCard";
import { HEATMAP_HORIZON_HOURS } from "../../Ramp/TemporalTest/heatmapConfig";
import { Shape } from "../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { ScheduledRampViewPresenter } from "./ScheduledRampViewPresenter";

const { useTransition } = React;

interface ScheduledRampViewProps {
  id: string;
  isReference?: boolean;
  isAlgorithmTesting?: boolean;
}

interface ScheduledRampViewState {
  scheduledRamp: Ramp | null;
  timestamp: Date;
}

/**
 * Calculate which spot an aircraft is in based on its position
 * Returns the full Shape object for the spot, or undefined if not in any spot
 */
const calculateSpotForPlacement = (
  placement: {
    x?: number | null;
    y?: number | null;
    angle?: number | null;
    aircraft_id?: string;
  },
  aircraftMap: Map<string, Aircraft>,
  rampShapes: Shape[],
  rampDepth: number
): Shape | undefined => {
  // Check if we have valid position data
  if (
    placement.x == null ||
    placement.y == null ||
    placement.angle == null ||
    !placement.aircraft_id
  ) {
    return undefined;
  }

  // Get aircraft data
  const aircraft = aircraftMap.get(placement.aircraft_id);
  if (!aircraft) {
    return undefined;
  }

  // Filter for spot shapes only
  const spotShapes = rampShapes.filter((shape) => shape.type === "Spot");
  if (spotShapes.length === 0) {
    return undefined;
  }

  // Build a map of legacy spots to their shape IDs
  const shapeIdMap = new Map<any, string>();

  // Convert spot shapes to LegacySpot format
  const legacySpots = spotShapes
    .filter(
      (shape) => shape.reference_points && shape.reference_points.length > 0
    )
    .map((shape) => {
      const legacySpot = {
        points: shape.points,
        referencePoints: shape.reference_points!,
      };
      shapeIdMap.set(legacySpot, shape.id);
      return legacySpot;
    });

  if (legacySpots.length === 0) {
    return undefined;
  }

  // Apply Y-flip transformation to placement coordinates
  // Database placements use Y-flipped coordinates (origin at top-left)
  // Spot shapes use non-flipped coordinates (origin at bottom-left)
  const transformedPosition = {
    x: placement.x,
    y: rampDepth - placement.y,
    angle: placement.angle,
  };

  // Create minimal tenant for spot detection
  const tenant = {
    aircraft,
    id: "",
    fbo_id: "",
    entity_id: "",
    tail_number: "", // Not available from Aircraft type
  } as Tenant;

  // Call getSpotForPositionLegacy with transformed coordinates
  try {
    const result = getSpotForPositionLegacy(
      tenant,
      transformedPosition,
      legacySpots
    );

    if (!result) {
      return undefined;
    }

    // Get the shape ID from the map
    const shapeId = shapeIdMap.get(result.spot);
    if (!shapeId) {
      return undefined;
    }

    // Find and return the matching shape
    return spotShapes.find((shape) => shape.id === shapeId);
  } catch (error) {
    console.error("Error detecting spot for placement:", error);
    return undefined;
  }
};

export const ScheduledRampView: React.FC<ScheduledRampViewProps> = React.memo(
  ({ id, isReference, isAlgorithmTesting }) => {
    // Predicate function to determine if a scheduled state change is meaningful for undo
    // Memoized to prevent infinite re-renders caused by changing function reference
    const isScheduledRampStateChangeMeaningful = React.useCallback(
      (
        oldState: ScheduledRampViewState,
        newState: ScheduledRampViewState
      ): boolean => {
        // If either state is null, it's meaningful
        if (!oldState?.scheduledRamp || !newState?.scheduledRamp) {
          return true;
        }

        // If timestamp changed, it's meaningful
        if (oldState.timestamp.getTime() !== newState.timestamp.getTime()) {
          return true;
        }

        // Check if ramp positions changed using existing logic
        const oldStateHash = md5(
          JSON.stringify(extractImportantLocationState(oldState.scheduledRamp))
        );
        const newStateHash = md5(
          JSON.stringify(extractImportantLocationState(newState.scheduledRamp))
        );

        return oldStateHash !== newStateHash;
      },
      [] // Empty deps - function has no external dependencies
    );
    // URL-driven timestamp management with current time as default
    const {
      currentTime: currentTimestamp,
      setCurrentTime: setCurrentTimestamp,
    } = useScheduleUrl({
      defaultDate: new Date(),
      updateUrlOnMount: true,
    });
    const navigate = useNavigate();
    const { postgrest } = useApi();
    const { activeFBO } = useActiveFBOs();
    const activeFBOId = activeFBO?.id; // Extract stable ID for dependency arrays
    const { predefinedObstacles } = usePredefinedObstacle();
    const { isDrawerOpen, setIsDrawerOpen } = useLayout();
    const { rangeStart, rangeEnd, setTimeRange, getDefaultWeekRange } =
      useScheduleTimeRange();
    const { preferences } = useUserPreferences();

    // Memoize PlacementService instance to avoid recreating on every render
    const placementService = React.useMemo(
      () => new PlacementService(postgrest),
      [postgrest]
    );

    // State for the base ramp structure and scheduled data
    const [baseRamp, setBaseRamp] = React.useState<Ramp | null>(null);

    // Track selected aircraft tail number separately (UI state, not persisted)
    // Using ref to avoid re-render loops and useEffect dependency issues
    const selectedTailNumberRef = React.useRef<string | undefined>(undefined);

    // Composite state for undo functionality (combines ramp state + timestamp)
    const [viewState, setViewState, undoViewState] =
      useStateHistory<ScheduledRampViewState>(
        { scheduledRamp: null, timestamp: currentTimestamp },
        isScheduledRampStateChangeMeaningful
      );

    // Use ref to keep track of currentTimestamp without causing re-renders
    const currentTimestampRef = React.useRef(currentTimestamp);
    React.useEffect(() => {
      currentTimestampRef.current = currentTimestamp;
    }, [currentTimestamp]);

    // Extract current values for easier access
    const scheduledRamp = viewState?.scheduledRamp;
    const setScheduledRamp = React.useCallback(
      (newRamp: Ramp | null) => {
        setViewState({
          scheduledRamp: newRamp,
          timestamp: currentTimestampRef.current,
        });
      },
      [setViewState]
    );

    // React 18 concurrent features for smooth date transitions
    const [isPending, startTransition] = useTransition();
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

    // State to trigger refresh
    const [refreshKey, setRefreshKey] = React.useState(0);

    // Track tenant ID -> trip ID mapping
    const [tenantTripMap, setTenantTripMap] = React.useState<
      Map<string, string>
    >(new Map());

    // State for all placements in the visible time range (for Active Trips metric)
    // This stores the raw placements from the database (without spot enrichment)
    const [rawPlacementsInRange, setRawPlacementsInRange] = React.useState<
      any[]
    >([]);
    // State for ALL placements in time range across all locations (for Timeline Aircraft)
    const [allPlacementsInTimeRange, setAllPlacementsInTimeRange] =
      React.useState<any[]>([]);
    // State for heatmap placements (72h window from current timestamp)
    const [heatmapPlacements, setHeatmapPlacements] = React.useState<any[]>([]);

    // State for trip counts by date (used for calendar date picker indicators)
    // Efficiently loaded from database aggregation instead of client-side computation
    const [tripCountsByDate, setTripCountsByDate] = React.useState<
      Map<string, number>
    >(new Map());

    // State for next/previous trip availability
    const [hasNextTrip, setHasNextTrip] = React.useState(false);
    const [hasPreviousTrip, setHasPreviousTrip] = React.useState(false);
    // Aircraft-specific trip availability
    const [hasNextTripForAircraft, setHasNextTripForAircraft] =
      React.useState(false);
    const [hasPreviousTripForAircraft, setHasPreviousTripForAircraft] =
      React.useState(false);

    // Use global snackbar
    const snackbar = useSnackbar();

    // State for Add Obstacle Dialog
    const [showAddObstacleDialog, setShowAddObstacleDialog] =
      React.useState(false);

    // State for user-added obstacles (separate from baseRamp to avoid circular dependency)
    const [addedObstacles, setAddedObstacles] = React.useState<any[]>([]);

    // Add logging wrapper for setAddedObstacles
    const setAddedObstaclesWithLogging = React.useCallback(
      (newObstaclesOrUpdater: any[] | ((prev: any[]) => any[])) => {
        if (typeof newObstaclesOrUpdater === "function") {
          setAddedObstacles((prev) => {
            const newValue = newObstaclesOrUpdater(prev);
            return newValue;
          });
        } else {
          setAddedObstacles(newObstaclesOrUpdater);
        }
      },
      []
    ); // Empty dependencies to prevent infinite loops

    // Load base ramp structure (dimensions, obstacles, etc.)
    React.useEffect(() => {
      if (!postgrest || !activeFBO || !id) {
        return;
      }

      const loadBaseRamp = async () => {
        try {
          const ramp = await fetchRamp(
            postgrest,
            activeFBO,
            id,
            isReference || false
          );
          setBaseRamp(ramp);
        } catch (error) {
          snackbar.notify({
            text: "Failed to load ramp structure",
            severity: "error",
          });
        }
      };

      loadBaseRamp();
    }, [postgrest, activeFBOId, id, isReference]); // Use activeFBOId instead of activeFBO to avoid infinite loops. snackbar intentionally omitted - only used in catch block

    // Load all placements in the visible time range (for Active Trips metric)
    React.useEffect(() => {
      if (!postgrest || !id || !rangeStart || !rangeEnd) return;

      const fetchPlacementsInRange = async () => {
        try {
          // Fetch placements for this specific ramp (for metrics)
          const placementsForThisRamp =
            await placementService.getPlacementsInTimeRange(
              rangeStart,
              rangeEnd,
              id,
              "ramp"
            );

          // Fetch ALL trips (including unassigned) for search capability (1 year range)
          // Uses the comprehensive view that includes trips without placement assignments
          // RampLeftPanel will filter this down to rangeStart/rangeEnd for timeline view
          // Use timeline's rangeEnd (not actual current date) to support viewing future dates
          const searchEndDate = new Date(rangeEnd);
          searchEndDate.setFullYear(searchEndDate.getFullYear() + 1); // 1 year ahead from timeline
          const allPlacements =
            await placementService.getAllTripsWithPlacementStatus(
              rangeStart, // from timeline range start (includes currently active trips)
              searchEndDate // to 1 year from now
            );

          // Fetch aircraft data for all placements in time range
          const aircraftIds = Array.from(
            new Set(
              placementsForThisRamp
                .map((p) => p.aircraft_id)
                .filter((id): id is string => id != null)
            )
          );

          const aircrafts = await fetchAircraftWithCache(
            postgrest,
            aircraftIds,
            activeFBO
          );
          const aircraftMap = new Map<string, Aircraft>();
          aircrafts.forEach((aircraft) => {
            aircraftMap.set(aircraft.id, aircraft);
          });

          // Enrich placements with aircraft data immediately
          const enrichedPlacements = placementsForThisRamp.map((p) => {
            const aircraft = p.aircraft_id
              ? aircraftMap.get(p.aircraft_id)
              : undefined;
            return {
              ...p,
              aircraft_make: aircraft?.make,
              aircraft_model: aircraft?.model,
            };
          });

          setRawPlacementsInRange(enrichedPlacements);
          setAllPlacementsInTimeRange(allPlacements);
        } catch (error) {
          // Silent failure - non-critical for main functionality
        }
      };

      fetchPlacementsInRange();
    }, [
      postgrest,
      id,
      placementService,
      rangeStart,
      rangeEnd,
      refreshKey,
      activeFBO,
    ]);

    // Enrich placementsInRange with spot data and aircraft details (memoized to avoid recalculation)
    const placementsInRange = React.useMemo(() => {
      if (!baseRamp || !rawPlacementsInRange.length || !scheduledRamp) {
        return rawPlacementsInRange;
      }

      // Build aircraft map from scheduledRamp tenants (already loaded with aircraft)
      const aircraftMap = new Map<string, Aircraft>();
      scheduledRamp.stack.tenants.forEach((t) => {
        if (t.aircraft && t.aircraft_id) {
          aircraftMap.set(t.aircraft_id, t.aircraft);
        }
      });

      // Enrich placements with spot detection ONLY (aircraft data already enriched in useEffect)
      return rawPlacementsInRange.map((p) => ({
        ...p,
        spot: calculateSpotForPlacement(
          p,
          aircraftMap,
          baseRamp.shapes,
          baseRamp.depth
        ),
      }));
    }, [baseRamp, rawPlacementsInRange, scheduledRamp]);

    // Load placements for heatmap (72h window from current timestamp)
    React.useEffect(() => {
      if (!postgrest || !id) return;

      const fetchHeatmapPlacements = async () => {
        try {
          const horizonHours =
            preferences?.heatmapHorizonHours || HEATMAP_HORIZON_HOURS;
          const heatmapStart = currentTimestamp;
          const heatmapEnd = new Date(
            currentTimestamp.getTime() + horizonHours * 60 * 60 * 1000
          );

          const placements = await placementService.getPlacementsInTimeRange(
            heatmapStart,
            heatmapEnd,
            id,
            "ramp"
          );

          setHeatmapPlacements(placements);
        } catch (error) {
          // Silent failure - heatmap is non-critical
          console.warn("Failed to load heatmap placements:", error);
        }
      };

      fetchHeatmapPlacements();
    }, [
      postgrest,
      id,
      placementService,
      currentTimestamp,
      refreshKey,
      preferences?.heatmapHorizonHours,
    ]);

    // Load trip counts by date for calendar indicators (1 year range)
    React.useEffect(() => {
      if (!postgrest || !id) return;

      const fetchTripCounts = async () => {
        try {
          const startDate = rangeStart || new Date();
          const endDate = new Date(startDate);
          endDate.setFullYear(endDate.getFullYear() + 1); // 1 year ahead for calendar

          const counts = await placementService.getTripCountsByDate(
            startDate,
            endDate,
            id,
            "ramp"
          );

          setTripCountsByDate(counts);
        } catch (error) {
          // Silent failure - calendar indicators are non-critical
          console.warn("Failed to load trip counts for calendar:", error);
        }
      };

      fetchTripCounts();
    }, [postgrest, id, placementService, rangeStart, refreshKey]);

    // Compute selected trip info BEFORE using it in useEffect below
    const selectedTripInfo = React.useMemo(() => {
      if (!scheduledRamp?.stack?.tenants) {
        return null;
      }

      // Find the selected tenant
      const selectedTenant = scheduledRamp.stack.tenants.find(
        (tenant) => tenant.selected
      );

      if (!selectedTenant) {
        return null;
      }

      // Get the trip ID for this tenant
      const tripId = tenantTripMap.get(selectedTenant.id);

      if (!tripId) {
        return null;
      }

      // Get ALL placements for this trip (may have multiple segments)
      // Use placementsInRange which contains all segments in the visible time range
      const tripPlacements = placementsInRange.filter(
        (placement) => placement.trip_id === tripId
      );

      if (tripPlacements.length === 0) {
        return null;
      }

      // Calculate earliest start and latest end across all segments
      const start = tripPlacements.reduce((earliest, p) => {
        const pStart = new Date(p.start_time);
        return pStart < earliest ? pStart : earliest;
      }, new Date(tripPlacements[0].start_time));

      const end = tripPlacements.reduce((latest, p) => {
        const pEnd = new Date(p.end_time);
        return pEnd > latest ? pEnd : latest;
      }, new Date(tripPlacements[0].end_time));

      return {
        start,
        end,
        tailNumber: selectedTenant.tail_number,
      };
    }, [scheduledRamp?.stack?.tenants, tenantTripMap, placementsInRange]);

    // Check for next/previous trips availability
    React.useEffect(() => {
      if (!placementService || !id || !currentTimestamp) return;

      const checkTripAvailability = async () => {
        try {
          // Always check location-wide trips
          const [nextTrip, previousTrip] = await Promise.all([
            placementService.getNextTripTime(id, "ramp", currentTimestamp),
            placementService.getPreviousTripTime(id, "ramp", currentTimestamp),
          ]);

          setHasNextTrip(!!nextTrip);
          setHasPreviousTrip(!!previousTrip);

          // If aircraft selected, also check aircraft-specific trips
          if (selectedTripInfo?.tailNumber) {
            const [nextAircraftTrip, previousAircraftTrip] = await Promise.all([
              placementService.getNextTripTimeForAircraft(
                selectedTripInfo.tailNumber,
                currentTimestamp,
                id,
                "ramp"
              ),
              placementService.getPreviousTripTimeForAircraft(
                selectedTripInfo.tailNumber,
                currentTimestamp,
                id,
                "ramp"
              ),
            ]);
            setHasNextTripForAircraft(!!nextAircraftTrip);
            setHasPreviousTripForAircraft(!!previousAircraftTrip);
          } else {
            // No aircraft selected, clear aircraft-specific state
            setHasNextTripForAircraft(false);
            setHasPreviousTripForAircraft(false);
          }
        } catch (error) {
          // Silent failure - non-critical for main functionality
        }
      };

      checkTripAvailability();
    }, [placementService, id, currentTimestamp, selectedTripInfo, refreshKey]);

    // Load scheduled placement data when timestamp changes
    React.useEffect(() => {
      if (!postgrest || !activeFBO || !baseRamp || !currentTimestamp) {
        // Don't set loading false here - let the data fetch complete first
        return;
      }

      const loadScheduledData = async () => {
        // Only show loading on initial load, keep previous data visible during date changes
        if (!scheduledRamp) {
          setInitialLoading(true);
        }
        // Don't set isUpdating during date changes - keep previous content visible
        try {
          // 1. Fetch placements at the current timestamp
          const allPlacements = await placementService.getPlacementsAtTime(
            currentTimestamp
          );

          // 2. Filter placements for this ramp
          // NOTE: We do NOT filter out placements with null x/y here!
          // Placements can exist without positions (they just haven't been parked yet)
          const rampPlacements = allPlacements.filter((p) => p.ramp_id === id);

          // 2.1 Deduplicate placements by placement.id first (detect AutoPark bugs)
          // This catches the case where the same placement_id appears for multiple trips
          const placementIdMap = new Map();
          const placementIdDuplicates: any[] = [];

          rampPlacements.forEach((placement) => {
            if (placementIdMap.has(placement.id)) {
              const existing = placementIdMap.get(placement.id);
              console.error(
                `⚠️  DUPLICATE PLACEMENT_ID DETECTED: placement_id="${placement.id}" appears in multiple trips:\n` +
                  `  - Trip ${existing.trip_id} (tail: ${existing.tail_number})\n` +
                  `  - Trip ${placement.trip_id} (tail: ${placement.tail_number})\n` +
                  `This indicates an AutoPark bug where the same position was assigned to multiple aircraft.`
              );
              placementIdDuplicates.push({ existing, duplicate: placement });
            } else {
              placementIdMap.set(placement.id, placement);
            }
          });

          // Use placement_id-deduplicated list for further processing
          const placementIdDeduplicated = Array.from(placementIdMap.values());

          // 2.2 Then deduplicate by trip_id (keep most recent placement per trip)
          const placementMap = new Map();
          placementIdDeduplicated.forEach((placement) => {
            const existing = placementMap.get(placement.trip_id);
            if (
              !existing ||
              new Date(placement.created_at) > new Date(existing.created_at)
            ) {
              placementMap.set(placement.trip_id, placement);
            }
          });

          const deduplicatedPlacements = Array.from(placementMap.values());

          // Log deduplication summary
          if (placementIdDuplicates.length > 0) {
            console.error(
              `[ScheduledRampView] Removed ${placementIdDuplicates.length} placement_id duplicates (AutoPark bug)`
            );
          }

          // 3. Extract unique aircraft IDs and fetch aircraft data
          const aircraftIds = deduplicatedPlacements
            .map((p) => p.aircraft_id)
            .filter((id) => id != null) as string[];

          const aircrafts =
            aircraftIds.length > 0
              ? await fetchAircraftWithCache(postgrest, aircraftIds, activeFBO)
              : [];

          // 4. Create aircraft lookup map
          const aircraftMap = new Map<string, Aircraft>();
          aircrafts.forEach((aircraft) => {
            aircraftMap.set(aircraft.id, aircraft);
          });

          // 6. Create scheduled stack ID - use stable ID to prevent remounting
          const scheduledStackId = `scheduled-${baseRamp.id}`;

          // Build trip ID map
          const tripMap = new Map<string, string>();

          // Use the separately tracked selected tail number (persists across data refreshes)
          const currentlySelectedTailNumber = selectedTailNumberRef.current;

          // Filter out placements with NULL aircraft_id (data integrity issue)
          // Show warning instead of crashing to allow UI to remain functional
          const validPlacements = deduplicatedPlacements.filter((p) => {
            if (!p.aircraft_id) {
              console.warn("Filtering out placement with NULL aircraft_id:", {
                placementId: p.id,
                tailNumber: p.tail_number,
                tripId: p.trip_id,
              });
              return false;
            }
            return true;
          });

          // Track how many placements were filtered for warning display
          const hiddenPlacementCount =
            deduplicatedPlacements.length - validPlacements.length;

          // Transform placements to tenants format
          const scheduledTenants: Tenant[] = validPlacements.map(
            (placement) => {
              const aircraft = placement.aircraft_id
                ? aircraftMap.get(placement.aircraft_id)
                : undefined;

              // At this point aircraft_id is guaranteed to be non-NULL due to filter above
              if (!placement.aircraft_id) {
                // This should never happen due to filter, but TypeScript doesn't know that
                throw new Error(
                  `Internal error: NULL aircraft_id passed filter (placement ${placement.id})`
                );
              }

              if (!aircraft) {
                console.error(
                  "Missing aircraft for placement with aircraft_id:",
                  {
                    placementId: placement.id,
                    aircraftId: placement.aircraft_id,
                    tailNumber: placement.tail_number,
                    tripId: placement.trip_id,
                    requestedIds: aircraftIds,
                    returnedIds: aircrafts.map((a) => a.id),
                    aircraftMapKeys: Array.from(aircraftMap.keys()),
                  }
                );
                throw new Error(
                  `Missing aircraft data for placement ${placement.id} (aircraft_id: ${placement.aircraft_id}, tail_number: ${placement.tail_number}). ` +
                    `This indicates a data integrity issue - aircraft records should exist for all scheduled placements.`
                );
              }

              const tenantId = `scheduled-trip-${placement.trip_id}`;

              // Store the mapping
              tripMap.set(tenantId, placement.trip_id);

              return {
                id: tenantId,
                fbo_id: baseRamp.fbo_id,
                entity_id: placement.entity_id || `entity-${placement.trip_id}`,
                placement_id: placement.id,
                aircraft_id: aircraft.id,
                type: placement.aircraft_type || "transient",
                tail_number: placement.tail_number || "Unknown",
                owner_manager: placement.owner_manager || "",
                trip_id: placement.trip_id,
                aircraft: aircraft,
                created_at: placement.start_time,
                // Preserve selection state for the same aircraft across time changes
                selected: currentlySelectedTailNumber === placement.tail_number,
                position: {
                  id: `position-${placement.id}`,
                  stack_id: scheduledStackId, // Use the scheduled stack ID, not the base ramp stack ID
                  entity_id:
                    placement.entity_id || `entity-${placement.trip_id}`,
                  x: placement.x,
                  y: placement.y,
                  angle: placement.angle || 0,
                  preferences: aircraft
                    ? defaultPreferenceForAircraft(aircraft, activeFBO)
                    : [],
                  timestamp: placement.start_time
                    ? new Date(placement.start_time).getTime()
                    : Date.now(), // Use placement start time (changes when position changes)
                },
                suggestedPositions: [],
                note: `Trip: ${placement.trip_id}`,
                note_last_updated_by: "scheduling",
                note_last_updated: new Date().toISOString(),
                placement_source: placement.placement_source,
                is_locked: placement.is_locked,
              };
            }
          );

          // Log any remaining duplicates for monitoring
          detectAndLogDuplicates(scheduledTenants, `ScheduledRampView-${id}`);

          // 6. Create scheduled stack

          // Merge base obstacles (from database) with user-added obstacles
          const allObstacles = [
            ...(baseRamp.stack.movableObstacles || []),
            ...addedObstacles,
          ];

          const scheduledMovableObstacles = allObstacles.map((obstacle) => {
            return {
              ...obstacle,
              position: {
                ...obstacle.position,
                stack_id: scheduledStackId, // Update to match scheduled stack ID
              },
            };
          });
          const scheduledStack: StackType = {
            id: scheduledStackId,
            name: `Scheduled View - ${currentTimestamp.toLocaleString()}`,
            location_id: baseRamp.id,
            isReference: false,
            tenants: scheduledTenants,
            movableObstacles: scheduledMovableObstacles,
            options: baseRamp.stack.options,
          };
          // 7. Create complete scheduled ramp object
          const scheduledRampData: Ramp = {
            ...baseRamp,
            stack: scheduledStack,
          };

          // Wrap state updates in startTransition to prevent remounting
          startTransition(() => {
            setScheduledRamp(scheduledRampData);
            setTenantTripMap(tripMap);
            setHasLoadedOnce(true);
          });
        } catch (error) {
          console.error("Error loading scheduled placement data:", error);
          snackbar.notify({
            text: "Failed to load scheduled placement data",
            severity: "error",
          });
          setHasLoadedOnce(true);
        } finally {
          setInitialLoading(false);
          setIsUpdating(false);
        }
      };

      loadScheduledData();
    }, [
      postgrest,
      activeFBOId, // Use activeFBOId instead of activeFBO to avoid infinite loops while still triggering on FBO changes
      baseRamp,
      currentTimestamp,
      id,
      refreshKey,
      addedObstacles,
      placementService,
      // selectedTailNumberRef - Intentionally omitted, refs don't need to be in dependencies
      // snackbar, // Intentionally omitted - only used in async catch blocks, safe to omit. Including it causes infinite re-renders since snackbar object is recreated on every render.
      // setScheduledRamp, // Intentionally omitted to prevent infinite re-renders. This function is behaviorally stable and only uses stable refs, so it's safe to exclude from dependencies.
      startTransition,
    ]);

    // Handler for time changes - now managed by URL hook which handles snapping
    const handleTimeChange = React.useCallback(
      (newTime: Date) => {
        setCurrentTimestamp(newTime); // URL hook handles snapping internally
        // Update the viewState to track time changes for undo
        if (scheduledRamp) {
          setViewState({ scheduledRamp, timestamp: newTime });
        }
      },
      [setCurrentTimestamp, scheduledRamp, setViewState]
    );

    // Handler for navigating to a different location
    const handleNavigateToLocation = React.useCallback(
      (locationId: string, locationType: string, time: Date) => {
        const path =
          locationType === "hangar"
            ? `/hangars/${locationId}/schedule`
            : `/ramps/${locationId}/schedule`;

        // Pass the exact time - the destination route will handle proper snapping
        const timeParam = time.toISOString();
        navigate(`${path}?t=${timeParam}`);
      },
      [navigate]
    );

    // Handler for jumping to next trip
    const handleJumpToNextTrip = React.useCallback(async () => {
      if (!placementService || !id) return;

      // Use selectedTripInfo which is already computed and has the tail number
      const selectedTailNumber = selectedTripInfo?.tailNumber;

      try {
        let nextTripTime: Date | null = null;

        if (selectedTailNumber) {
          // If an aircraft is selected, jump to its next trip
          nextTripTime = await placementService.getNextTripTimeForAircraft(
            selectedTailNumber,
            currentTimestamp,
            id,
            "ramp"
          );
        } else {
          // Otherwise, jump to the next trip at this location
          nextTripTime = await placementService.getNextTripTime(
            id,
            "ramp",
            currentTimestamp
          );
        }

        if (nextTripTime) {
          // Update the time to jump to the next trip, snapped to 15-minute grid
          // Use ceil for next trips to ensure we're at a time when the trip is active
          const snappedTime = ceilToQuarterHour(nextTripTime);
          setCurrentTimestamp(snappedTime);

          // Recalibrate the time range to center around the new time
          const { start: newStart, end: newEnd } =
            getDefaultWeekRange(snappedTime);
          setTimeRange(newStart, newEnd);
        }
      } catch (error) {
        console.error("Failed to jump to next trip:", error);
        snackbar.notify({
          text: "Failed to jump to next trip",
          severity: "error",
        });
      }
    }, [
      placementService,
      id,
      currentTimestamp,
      setCurrentTimestamp,
      getDefaultWeekRange,
      setTimeRange,
      // snackbar, // Intentionally omitted - only used in error catch block
      selectedTripInfo,
    ]);

    // Handler for jumping to previous trip
    const handleJumpToPreviousTrip = React.useCallback(async () => {
      if (!placementService || !id) return;

      // Use selectedTripInfo which is already computed and has the tail number
      const selectedTailNumber = selectedTripInfo?.tailNumber;

      try {
        let previousTripTime: Date | null = null;

        if (selectedTailNumber) {
          // If an aircraft is selected, jump to its previous trip
          previousTripTime =
            await placementService.getPreviousTripTimeForAircraft(
              selectedTailNumber,
              currentTimestamp,
              id,
              "ramp"
            );
        } else {
          // Otherwise, jump to the previous trip at this location
          previousTripTime = await placementService.getPreviousTripTime(
            id,
            "ramp",
            currentTimestamp
          );
        }

        if (previousTripTime) {
          // Update the time to jump to the previous trip - URL hook handles snapping
          setCurrentTimestamp(previousTripTime);

          // Recalibrate the time range to center around the new time
          const { start: newStart, end: newEnd } =
            getDefaultWeekRange(previousTripTime);
          setTimeRange(newStart, newEnd);
        }
      } catch (error) {
        console.error("Failed to jump to previous trip:", error);
        snackbar.notify({
          text: "Failed to jump to previous trip",
          severity: "error",
        });
      }
    }, [
      placementService,
      id,
      currentTimestamp,
      setCurrentTimestamp,
      getDefaultWeekRange,
      setTimeRange,
      // snackbar, // Intentionally omitted - only used in error catch block
      selectedTripInfo,
    ]);

    // Handler for undo - restores both ramp state and timestamp
    const handleUndo = React.useCallback(() => {
      const previousState = undoViewState();
      if (previousState) {
        // Restore the timestamp first
        setCurrentTimestamp(previousState.timestamp);
        // The ramp state is already restored by undoViewState
        // Trigger a refresh to ensure UI is consistent
        setRefreshKey((prev) => prev + 1);
      }
    }, [undoViewState, setCurrentTimestamp, setRefreshKey]);

    // Handler for ramp changes (when user drags aircraft in scheduled view)
    const handleRampChange = React.useCallback(
      async (updatedRamp: Ramp) => {
        // Check if any tenant is selected in the updated ramp
        const selectedInUpdate = updatedRamp?.stack?.tenants?.find(
          (t) => t.selected
        );

        // Update selection ref (only when explicitly changed, not from data refresh)
        const newSelection = selectedInUpdate?.tail_number;

        // Update ref based on selection state
        if (selectedInUpdate) {
          selectedTailNumberRef.current = newSelection;
        } else {
          // Clear ref when no selection (handles deselection via useUnselect hook)
          selectedTailNumberRef.current = undefined;
        }

        // ALWAYS apply the ref value when updating scheduledRamp (preserves selection across refreshes)
        const currentSelection = selectedTailNumberRef.current;
        setScheduledRamp({
          ...updatedRamp,
          stack: {
            ...updatedRamp.stack,
            tenants: updatedRamp.stack.tenants.map((t) => {
              // Just apply selection, preserve everything else from incoming data
              return {
                ...t,
                selected: currentSelection
                  ? t.tail_number === currentSelection
                  : false,
              };
            }),
          },
        });

        if (!scheduledRamp || !postgrest) return;

        try {
          // Get current placements for comparison
          const oldTenants = scheduledRamp.stack.tenants;
          const newTenants = updatedRamp.stack.tenants;
          const oldObstacles = scheduledRamp.stack.movableObstacles;
          const newObstacles = updatedRamp.stack.movableObstacles;

          // Handle aircraft/tenant removals first
          for (const oldTenant of oldTenants) {
            const stillExists = newTenants.find((t) => t.id === oldTenant.id);

            if (!stillExists) {
              // Tenant was removed
              const tripId = tenantTripMap.get(oldTenant.id);

              if (tripId) {
                // Remove from location using the placement service
                const startTimeISO = currentTimestamp.toISOString();
                await placementService.assignToLocationWithGapFilling(
                  tripId,
                  null, // null location means unassigned
                  null,
                  startTimeISO,
                  undefined,
                  true, // excludeConflicts
                  { x: null, y: null, angle: null }
                );
              }
            }
          }

          // Handle aircraft/tenant position changes
          for (const newTenant of newTenants) {
            const oldTenant = oldTenants.find((t) => t.id === newTenant.id);

            if (!oldTenant) continue; // Skip new tenants (shouldn't happen in scheduled view)

            const oldPos = oldTenant.position;
            const newPos = newTenant.position;

            // Check if position changed (x, y, angle)
            const positionChanged =
              oldPos.x !== newPos.x ||
              oldPos.y !== newPos.y ||
              oldPos.angle !== newPos.angle;

            if (positionChanged) {
              // Get trip ID from our map
              const tripId = tenantTripMap.get(newTenant.id);

              if (!tripId) {
                continue;
              }

              // Use database function to handle the placement change atomically
              // This ensures the aircraft stays in the new position until explicitly moved again
              await placementService.moveAircraftAtTime(
                tripId,
                id, // ramp id
                "ramp",
                currentTimestamp, // When the move happens
                {
                  x: newPos.x,
                  y: newPos.y,
                  angle: newPos.angle,
                },
                newTenant.is_locked ?? true // Only lock if in spot/zone
              );
            }
          }

          // Handle movable obstacle position changes
          for (const newObstacle of newObstacles) {
            const oldObstacle = oldObstacles.find(
              (o) => o.id === newObstacle.id
            );

            if (!oldObstacle) continue; // Skip new obstacles (shouldn't happen in scheduled view)

            const oldPos = oldObstacle.position;
            const newPos = newObstacle.position;

            // Check if obstacle position changed (x, y, angle)
            const obstaclePositionChanged =
              oldPos.x !== newPos.x ||
              oldPos.y !== newPos.y ||
              oldPos.angle !== newPos.angle;

            if (obstaclePositionChanged) {
              // Check if this obstacle is from base or added obstacles
              const isFromBase = baseRamp?.stack.movableObstacles.find(
                (mo) => mo.id === newObstacle.id
              );
              const isFromAdded = addedObstacles.find(
                (mo) => mo.id === newObstacle.id
              );

              if (isFromBase && baseRamp) {
                // Update the base ramp's movable obstacle position
                const updatedBaseRamp = {
                  ...baseRamp,
                  stack: {
                    ...baseRamp.stack,
                    movableObstacles: baseRamp.stack.movableObstacles.map(
                      (mo) => (mo.id === newObstacle.id ? newObstacle : mo)
                    ),
                  },
                };
                setBaseRamp(updatedBaseRamp);
              } else if (isFromAdded) {
                // Update the added obstacles state
                setAddedObstaclesWithLogging(
                  addedObstacles.map((mo) =>
                    mo.id === newObstacle.id ? newObstacle : mo
                  )
                );
              }

              // Save obstacle position change to database immediately
              try {
                await postgrest.from("position").upsert({
                  id: newObstacle.position.id,
                  stack_id: baseRamp?.stack.id, // Use base stack ID for persistence
                  entity_id: newObstacle.entity_id,
                  x: newObstacle.position.x,
                  y: newObstacle.position.y,
                  angle: newObstacle.position.angle,
                  preferences: newObstacle.position.preferences || [],
                });
              } catch (error: any) {
                snackbar.notify({
                  text: "Failed to save obstacle position",
                  severity: "error",
                });
              }
            }

            // Handle movable obstacle removals
            const currentObstacleCount =
              scheduledRamp.stack.movableObstacles.length;
            const newObstacleCount = updatedRamp.stack.movableObstacles.length;

            if (newObstacleCount < currentObstacleCount) {
              const removedObstacles =
                scheduledRamp.stack.movableObstacles.filter(
                  (oldObstacle) =>
                    !updatedRamp.stack.movableObstacles.find(
                      (newObs) => newObs.id === oldObstacle.id
                    )
                );

              for (const removed of removedObstacles) {
                const isFromBase = baseRamp?.stack.movableObstacles.find(
                  (base) => base.id === removed.id
                );

                if (isFromBase && baseRamp) {
                  await postgrest
                    .from("position")
                    .delete()
                    .eq("id", removed.position.id);
                  await postgrest
                    .from("movable_obstacle")
                    .delete()
                    .eq("id", removed.id);

                  const updatedBaseRamp = {
                    ...baseRamp,
                    stack: {
                      ...baseRamp.stack,
                      movableObstacles: baseRamp.stack.movableObstacles.filter(
                        (obs) => obs.id !== removed.id
                      ),
                    },
                  };
                  setBaseRamp(updatedBaseRamp);
                }
              }
            }
          }
        } catch (error: any) {
          // Error handling via snackbar if needed
          snackbar.notify({
            text: error.message || "Failed to save changes",
            severity: "error",
          });
        }
      },
      [
        scheduledRamp,
        baseRamp,
        setBaseRamp,
        postgrest,
        currentTimestamp,
        id,
        tenantTripMap,
        // snackbar, // Intentionally omitted - only used in error catch blocks
        placementService,
        setScheduledRamp,
        addedObstacles,
        setAddedObstaclesWithLogging,
      ]
    );

    // Handler for aircraft removal - only updates local state
    // The database update happens through handleRampChange
    const handleAircraftRemoval = React.useCallback(
      (tenantId: string) => {
        if (!scheduledRamp) return;

        // Update the local state (this will be captured for undo)
        const updatedRamp = {
          ...scheduledRamp,
          stack: {
            ...scheduledRamp.stack,
            tenants: scheduledRamp.stack.tenants.filter(
              (t) => t.id !== tenantId
            ),
          },
        };

        // Update through handleRampChange which will:
        // 1. Update local state (for immediate UI feedback)
        // 2. Trigger database sync (handleRampChange handles this)
        // 3. Enable proper undo (state is tracked before change)
        handleRampChange(updatedRamp);
      },
      [scheduledRamp, handleRampChange]
    );

    // Navigation options for the location dropdown
    const isSchedulingEnabled = activeFBO?.settings?.enableScheduling ?? false;

    return (
      <ScheduledRampViewPresenter
        id={id}
        isReference={isReference}
        isAlgorithmTesting={isAlgorithmTesting}
        baseRamp={baseRamp}
        scheduledRamp={scheduledRamp}
        currentTimestamp={currentTimestamp}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        setTimeRange={setTimeRange}
        initialLoading={initialLoading}
        isUpdating={isUpdating}
        isPending={isPending}
        hasLoadedOnce={hasLoadedOnce}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        isSchedulingEnabled={isSchedulingEnabled}
        hasNextTrip={
          selectedTripInfo?.tailNumber ? hasNextTripForAircraft : hasNextTrip
        }
        hasPreviousTrip={
          selectedTripInfo?.tailNumber
            ? hasPreviousTripForAircraft
            : hasPreviousTrip
        }
        selectedTripInfo={selectedTripInfo}
        tenantTripMap={tenantTripMap}
        placementsInRange={placementsInRange}
        tripCountsByDate={tripCountsByDate}
        allPlacementsForSearch={allPlacementsInTimeRange}
        heatmapPlacements={heatmapPlacements}
        showAddObstacleDialog={showAddObstacleDialog}
        setShowAddObstacleDialog={setShowAddObstacleDialog}
        addedObstacles={addedObstacles}
        predefinedObstacles={predefinedObstacles}
        handleTimeChange={handleTimeChange}
        handleJumpToNextTrip={handleJumpToNextTrip}
        handleJumpToPreviousTrip={handleJumpToPreviousTrip}
        handleUndo={handleUndo}
        handleRampChange={handleRampChange}
        handleAircraftRemoval={handleAircraftRemoval}
        handleNavigateToLocation={handleNavigateToLocation}
        setAddedObstaclesWithLogging={setAddedObstaclesWithLogging}
        placementService={placementService}
        refreshKey={refreshKey}
        setRefreshKey={setRefreshKey}
      />
    );
  }
);
